/**
 * The pieces every Apps surface shares: the organization's app entries (from
 * real, org-scoped connection state), an app's icon, and the link that opens
 * it. The Apps page, the Apps sidebar and Home's sidebar all use these, so an
 * app looks and opens the same everywhere.
 *
 * @module components/apps/AppParts
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { FileCheckIcon, RadarIcon, WebhookIcon } from "lucide-react";
import { useMemo, type MouseEvent, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { useCustomMcpServers } from "../../state/customMcpServers";
import { useIntegrations } from "../../state/governance";
import { readAppResumeHref } from "./appAreaGates";
import { buildAppEntries, PROBE_APP_ID, type AppEntry } from "./appRegistry";
import { useIManageStatusQuery } from "./imanage/imanageQueries";
import { CustomMcpServerIcon } from "./integrations/CustomMcpServerRow";
import { IntegrationIcon } from "./integrations/IntegrationIcon";

export interface AppEntriesView {
  readonly entries: ReadonlyArray<AppEntry>;
  /** True until the integrations catalogue has answered once. */
  readonly isPending: boolean;
}

/**
 * The organization's apps, read from its integrations, the member's iManage
 * connection and its MCP servers. The iManage status is the area's own cached
 * read, so the launcher and the area agree, and opening a connected iManage
 * from here needs no second status read before the app shows.
 */
export function useAppEntries(organizationId: string | null): AppEntriesView {
  const { snapshot, isPending } = useIntegrations(organizationId);
  const { data: personal } = useIManageStatusQuery(organizationId);
  const { servers } = useCustomMcpServers(organizationId);
  const entries = useMemo(
    () =>
      buildAppEntries({
        integrations: snapshot?.integrations ?? null,
        personal: personal
          ? {
              connected: personal.connected === true,
              configured: personal.configured === true,
              pending: personal.pending === true,
            }
          : null,
        customServers: servers,
      }),
    [personal, servers, snapshot],
  );
  return { entries, isPending: isPending && snapshot === null };
}

/** An app's tile: vendor artwork for integrations, Amoreg's own mark for first-party apps. */
export function AppIcon({
  entry,
  className,
}: {
  readonly entry: AppEntry;
  readonly className?: string;
}) {
  switch (entry.kind) {
    case "first-party":
      return (
        <span
          className={cn(
            "flex shrink-0 items-center justify-center rounded-[10px] border border-border/60 bg-primary/10 text-primary",
            className,
          )}
        >
          <FirstPartyGlyph id={entry.id} className="size-[55%]" />
        </span>
      );
    case "custom":
      return <CustomMcpServerIcon className={className} />;
    case "integration":
      return <IntegrationIcon integration={entry.integration} className={className} />;
    case "coming-soon":
      return (
        <IntegrationIcon
          integration={{ id: entry.id, name: entry.name, iconUrl: null, toolkitSlug: null }}
          className={cn("opacity-60 grayscale", className)}
        />
      );
  }
}

/** An app's mark at sidebar-row size: a plain glyph, or the vendor's logo at 16px. */
export function AppGlyph({ entry }: { readonly entry: AppEntry }) {
  switch (entry.kind) {
    case "first-party":
      return <FirstPartyGlyph id={entry.id} />;
    case "custom":
      return <WebhookIcon />;
    case "integration":
    case "coming-soon":
      return (
        <IntegrationIcon
          integration={
            entry.kind === "integration"
              ? entry.integration
              : { id: entry.id, name: entry.name, iconUrl: null, toolkitSlug: null }
          }
          className="size-4 rounded-[3px] border-0 bg-transparent p-0"
        />
      );
  }
}

/**
 * A link that opens an app. Contract Intelligence and custom servers have
 * their own routes; every catalogue integration opens at `/$org/apps/$appId`,
 * and a connected app with a landing (iManage) at its last view.
 */
export function AppLink({
  entry,
  orgSegment,
  className,
  onClick,
  children,
  ...rest
}: {
  readonly entry: AppEntry;
  readonly orgSegment: string;
  readonly className?: string;
  readonly onClick?: () => void;
  readonly children?: ReactNode;
  readonly "aria-label"?: string;
  readonly [data: `data-${string}`]: string | undefined;
}) {
  const navigate = useNavigate();
  // A connected app with a landing reopens where the person left it
  // (appAreaGates); a modified click keeps the link's own address.
  const resume = (event: MouseEvent<HTMLAnchorElement>) => {
    if (entry.availability !== "connected" || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const href = readAppResumeHref(orgSegment, entry.id);
    if (!href) return;
    event.preventDefault();
    void navigate({ href });
  };
  if (entry.kind === "first-party") {
    return (
      <Link
        to="/$org/apps/$appId"
        params={{ org: orgSegment, appId: entry.id }}
        className={className}
        onClick={onClick}
        {...rest}
      >
        {children}
      </Link>
    );
  }
  if (entry.kind === "custom") {
    return (
      <Link
        to="/$org/apps/custom/$serverId"
        params={{ org: orgSegment, serverId: entry.id }}
        className={className}
        onClick={onClick}
        {...rest}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link
      to="/$org/apps/$appId"
      params={{ org: orgSegment, appId: entry.id }}
      className={className}
      onClick={(event) => {
        onClick?.();
        resume(event);
      }}
      {...rest}
    >
      {children}
    </Link>
  );
}

/** The glyph of one of Amoreg's own apps. */
export function FirstPartyGlyph({ id, className }: { readonly id: string; readonly className?: string }) {
  return id === PROBE_APP_ID ? <RadarIcon className={className} /> : <FileCheckIcon className={className} />;
}
